import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PageContainer from '@components/Layout/PageContainer';
import PageHeader from '@features/PageHeader';
import { Typography } from '@components/Typography';
import Button from '@components/Button';
import { ModalConfirm } from '@components/Modal';
import { CardsGrid } from '@components/CardItem/styles';
import { useHasAccessToAsset, useTranslation } from '@hooks';
import usePermissions from '@hooks/usePermissions';
import { alertActions, brandsActions, formsActions, labelsActions } from '@actions';
import { formsService } from '@services';
import { handleApiError } from '@helpers/api';
import { downloadBlobAs } from '@helpers/download';
import { VIEW_TYPE } from '@constants/microsites';
import { toSubmissionsFileName } from './helpers';
import { FormsIcon } from 'assets/images/icons';
import FormItem from './FormItem';
import EditModal from './EditModal';

const FormsPage = () => {
    const { t } = useTranslation();
    const [modal, setModal] = useState({ open: false, type: VIEW_TYPE.CREATE });
    const [selectedForm, setSelectedForm] = useState(null);
    const [formToDelete, setFormToDelete] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [downloadingFormId, setDownloadingFormId] = useState(null);

    const { hasFullAccess } = usePermissions();
    const { hasAccessToAssetById } = useHasAccessToAsset();
    const account = useSelector(state => state.account?.account);
    const data = useSelector(state => state.forms.data);
    const brands = useSelector(state => state.brands.data);
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(formsActions.getAll());
        dispatch(labelsActions.get());
        dispatch(brandsActions.getAll());
    }, [dispatch]);

    const canManage = hasFullAccess('microsites');

    const onCloseModal = () => {
        setModal({ open: false, type: VIEW_TYPE.CREATE });
        setSelectedForm(null);
    };

    const onSave = async model => {
        setIsSubmitting(true);
        try {
            await formsService.saveForm(model);
            await dispatch(formsActions.getAll());
            dispatch(alertActions.success(t(model.id ? 'alertMessages.editSuccess' : 'alertMessages.createSuccess')));
            onCloseModal();
        } catch (error) {
            handleApiError(error, model.id ? 'alertMessages.editFail' : 'alertMessages.createFail');
        } finally {
            setIsSubmitting(false);
        }
    };

    const onDelete = async () => {
        try {
            await formsService.deleteForm(formToDelete.id);
            await dispatch(formsActions.getAll());
            dispatch(alertActions.success(t('alertMessages.deleteSuccess')));
            setFormToDelete(null);
            if (modal.open) onCloseModal();
        } catch (error) {
            handleApiError(error, 'alertMessages.deleteFail');
        }
    };

    const onDownloadSubmissions = async form => {
        setDownloadingFormId(form.id);
        try {
            const blobUrl = await formsService.exportSubmissions(form.id);
            downloadBlobAs(blobUrl, toSubmissionsFileName(form.internalName));
        } catch (error) {
            handleApiError(error, 'common.exportError');
        } finally {
            setDownloadingFormId(null);
        }
    };

    const openModal = (form, type) => {
        setSelectedForm(form);
        setModal({ open: true, type });
    };

    return (
        <PageContainer>
            <PageHeader
                styles={{ marginBottom: '16px' }}
                icon={<FormsIcon />}
                title={t('FormsPage.title')}
                subtitle={t('FormsPage.subtitle')}
                metrics={
                    <Typography variant="body1" sx={{ color: 'var(--text-secondary)' }}>
                        {t('FormsPage.created')} {data?.totalCount ?? 0}
                    </Typography>
                }
            >
                {canManage && (
                    <Button
                        variant="contained"
                        onClick={() => openModal(null, VIEW_TYPE.CREATE)}
                        disabled={isSubmitting}
                        id="pl-button-create-form"
                    >
                        {t('FormsPage.createForm')}
                    </Button>
                )}
            </PageHeader>
            <CardsGrid>
                {data?.forms?.map(form => (
                    <FormItem
                        key={form.id}
                        form={form}
                        canWrite={hasAccessToAssetById(form, 'microsites').w}
                        isSubmitting={isSubmitting || downloadingFormId === form.id}
                        onClickEdit={() => openModal(form, VIEW_TYPE.EDIT)}
                        onClickDownload={() => onDownloadSubmissions(form)}
                        onClickView={() => openModal(form, VIEW_TYPE.VIEW)}
                        onClickDelete={() => setFormToDelete(form)}
                    />
                ))}
            </CardsGrid>
            {modal.open ? (
                <EditModal
                    modal={modal}
                    account={account}
                    brands={brands}
                    selectedForm={selectedForm}
                    onClose={onCloseModal}
                    onSave={onSave}
                    onDelete={() => setFormToDelete(selectedForm)}
                    isUser={selectedForm ? !hasAccessToAssetById(selectedForm, 'microsites').w : !canManage}
                />
            ) : null}
            {formToDelete ? (
                <ModalConfirm
                    isOpen
                    handleClose={() => setFormToDelete(null)}
                    title={t('common.deleteTitle')}
                    description={t('common.deleteDescription')}
                    onPrimaryAction={onDelete}
                    primaryActionText={t('FormsPage.deleteForm')}
                    primaryActionType="danger"
                />
            ) : null}
        </PageContainer>
    );
};

export default FormsPage;
